/**
 * TodayUsageWidget — Anteils-Ring auf dem Dashboard.
 *
 * Dünner App-Wrapper um die geteilte TodayUsageCard (core-frontend
 * plugin-usage): konzentrische Ringe = Anteil je Event-Typ am Gesamtvolumen,
 * Zentrum = Gesamtzahl. Labels kommen aus dem usage-Namespace (eventTypes-
 * Katalog + todayCard-Gruppe).
 *
 * Der Zeitraum ist **nicht mehr fest „heute"**, sondern der im großen
 * Nutzungs-Widget gewählte (`dashboardRange.ts`). Ring und Verlaufsdiagramm
 * zeigen damit dieselbe Spanne, statt zwei Zahlen nebeneinander zu stellen, die
 * sich auf verschiedene Zeiträume beziehen.
 *
 * 🔴 Deshalb trägt dieses Widget seine Überschrift **selbst** und ist ohne
 * `titleKey` registriert: Der WidgetContainer kann nur einen festen Titel
 * setzen, und „Heute" wäre bei 30 Tagen schlicht falsch. Die Beschriftung kommt
 * aus denselben i18n-Schlüsseln wie der Umschalter.
 */

import { TodayUsageCard } from '@xhubio-saas/core-frontend/plugin-usage'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useUsageClient } from '../../../api/usageClient'
import { useEventTypeLabel } from '../../../lib/eventTypeLabel'
import { rangeLabelKey, rangeToWindow, useDashboardRange } from './dashboardRange'

export function TodayUsageWidget() {
  const client = useUsageClient()
  // Karten-Texte (todayCard.*) liegen im usage-Namespace des geteilten Pakets,
  // Zeitraum- und Event-Typ-Labels im common-Namespace der App — daher zwei
  // Übersetzer.
  const { t } = useTranslation('usage')
  const { t: tCommon } = useTranslation()

  const range = useDashboardRange()
  const window = useMemo(() => rangeToWindow(range), [range])
  const label = rangeLabelKey(range)

  // Derselbe Haken wie im Diagramm daneben — sonst hiesse dieselbe Serie hier
  // anders als dort (siehe lib/eventTypeLabel.ts).
  const eventTypeLabel = useEventTypeLabel()

  return (
    <div className="flex h-full flex-col" data-testid="today-usage-widget">
      <h3
        className="text-sm font-medium text-muted-foreground"
        data-testid="today-usage-range-label"
      >
        {tCommon(label.key, label.fallback)}
      </h3>
      <TodayUsageCard
        client={client}
        eventTypeLabel={eventTypeLabel}
        range={window}
        translations={{
          // Überschrift trägt dieses Widget selbst (siehe oben) — Karte ohne eigene.
          title: '',
          centerLabel: t('todayCard.centerLabel', 'calls'),
          othersLabel: t('todayCard.othersLabel', 'Other'),
          empty: t('todayCard.empty', 'No calls yet today.'),
        }}
        // Five rings (4 + "Other") at the default 190 px left a ~46 px hole —
        // the inner arcs ran through "226 Aufrufe". 240 px gives a ~96 px hole;
        // index.css lets the ring shrink to the widget width and drops the
        // card's side padding, so it never overflows the small widget.
        size={240}
        className="flex-1 border-0 shadow-none"
      />
    </div>
  )
}
